// Routing engine: builds every Layer 3 device's RIB from connected and static
// routes plus RIP, OSPF, EIGRP and BGP.
//
// Like the rest of the simulator this is a *behavioural* model. It does not
// exchange packets; instead it applies the rules a real router applies:
//   * which interfaces a protocol runs on (network statements, passive-interface)
//   * whether two routers become neighbours (same subnet, area, AS, version, peer config)
//   * what gets advertised (connected prefixes, auto-summary, BGP network statements)
//   * how the best path is chosen (metric, then administrative distance)
// Every show command, ping and traceroute is derived from the result, so CLI
// output always agrees with what traffic actually does.
//
// Results are memoised per network "routing signature" (config + link state),
// so repeated lookups during a ping do not recompute convergence.

import { ipToInt, intToIp, maskToPrefix, prefixToMask, networkOf, inSubnet, classfulPrefix, wildcardMatch } from './addr.js';
import { l2Flood } from './l2.js';

export const AD = { C: 0, L: 0, S: 1, eBGP: 20, EIGRP: 90, OSPF: 110, RIP: 120, iBGP: 200, OSPF_EXT: 110 };
const INF = Number.POSITIVE_INFINITY;

// ------------------------------------------------------------ interface facts
export const isLoopback = (n) => /^Loopback\d+$/.test(n);
/** Default bandwidth (kbps) and delay (tens of µs), as IOS shows them. */
export function ifBandwidth(n, i) {
  if (i?.bandwidth) return i.bandwidth;
  if (isLoopback(n)) return 8000000;
  if (/^FastEthernet/.test(n)) return 100000;
  if (/^Serial/.test(n)) return 1544;
  return 1000000;
}
export function ifDelay(n, i) {
  if (i?.delay) return i.delay;
  if (isLoopback(n)) return 500;
  if (/^FastEthernet/.test(n)) return 10;
  if (/^Serial/.test(n)) return 2000;
  return 1;
}
/** OSPF cost = reference bandwidth (100 Mbps) / interface bandwidth, minimum 1. */
export function ospfCost(n, i) {
  if (i?.ospfCost) return i.ospfCost;
  return Math.max(1, Math.floor(100000 / ifBandwidth(n, i)));
}
export const eigrpMetric = (bwKbps, delaySum) => 256 * (Math.floor(10000000 / bwKbps) + delaySum);

const isRouterLike = (d) => d && (d.kind === 'router' || (d.kind === 'switch' && !!d.ipRouting));

/**
 * The simulator hands us its own primitives (to avoid an import cycle):
 *   { l3Addr(net,id,ifc), ifStatus(net,id,ifc) }
 */
let SIM = null;
export function bindSim(fns) { SIM = fns; }

/** Up interfaces of a device that carry IPv4. */
function upIfs(net, id) {
  const d = net.devices[id];
  const out = [];
  for (const n of Object.keys(d.interfaces || {})) {
    const a = SIM.l3Addr(net, id, n);
    if (!a) continue;
    const prefix = maskToPrefix(a.mask);
    out.push({ ifc: n, ip: a.ip, mask: a.mask, prefix: isLoopback(n) ? 32 : prefix, realPrefix: prefix, net: networkOf(a.ip, isLoopback(n) ? 32 : prefix), conNet: networkOf(a.ip, prefix), i: d.interfaces[n] });
  }
  return out;
}

/** Other L3 devices on the same broadcast domain/subnet as (id, ifc). */
function segmentPeers(net, id, ifc, a) {
  if (isLoopback(ifc)) return [];
  const peers = [];
  for (const r of l2Flood(net, id, ifc)) {
    if (r.dev === id || !isRouterLike(net.devices[r.dev])) continue;
    const b = SIM.l3Addr(net, r.dev, r.ifc);
    if (!b || !inSubnet(b.ip, a.conNet, a.realPrefix)) continue;
    peers.push({ dev: r.dev, ifc: r.ifc, ip: b.ip, mask: b.mask });
  }
  return peers;
}

function highestIp(ips) { return ips.length ? ips.reduce((m, ip) => (ipToInt(ip) > ipToInt(m) ? ip : m)) : null; }
/** IOS router-ID rule: configured → highest loopback → highest active interface. */
export function routerId(net, id, configured) {
  if (configured) return configured;
  const ifs = upIfs(net, id);
  return highestIp(ifs.filter((x) => isLoopback(x.ifc)).map((x) => x.ip)) || highestIp(ifs.map((x) => x.ip));
}

// ------------------------------------------------------------ base RIB (connected + static)
function baseRoutes(net, id) {
  const d = net.devices[id];
  const out = [];
  for (const a of upIfs(net, id)) {
    if (!isLoopback(a.ifc)) out.push({ code: 'C', net: a.conNet, prefix: a.realPrefix, ifc: a.ifc, ad: 0, metric: 0, src: 'connected' });
    else out.push({ code: 'C', net: a.ip, prefix: 32, ifc: a.ifc, ad: 0, metric: 0, src: 'connected' });
    if (!isLoopback(a.ifc)) out.push({ code: 'L', net: a.ip, prefix: 32, ifc: a.ifc, ad: 0, metric: 0, src: 'local' });
  }
  const connected = out.filter((r) => r.code === 'C');
  for (const s of d.routes || []) {
    const p = maskToPrefix(s.mask);
    let ifc = s.ifc || null;
    if (s.nh) {
      const via = connected.find((c) => inSubnet(s.nh, c.net, c.prefix) && c.prefix < 32);
      if (!via) continue; // next hop not resolvable → route not installed
      ifc = via.ifc;
    } else if (ifc === 'Null0') {
      // discard route (summary / BGP anchor): always installed
    } else if (!ifc || !SIM.l3Addr(net, id, ifc)) continue;
    out.push({ code: p === 0 ? 'S*' : 'S', net: s.net, prefix: p, nh: s.nh || null, ifc, ad: s.ad || 1, metric: 0, exitOnly: !s.nh, src: 'static' });
  }
  return out;
}

// ------------------------------------------------------------ RIP
function ripEnabled(d, a) {
  const r = d.rip;
  if (!r) return false;
  const cp = classfulPrefix(a.ip);
  return r.networks.some((n) => networkOf(n, classfulPrefix(n)) === networkOf(a.ip, cp));
}
const ripAccepts = (rx, txVersion) => (rx.version ? rx.version === txVersion : true);
const ripSends = (r) => r.version || 1;

function runRip(net, ids, ribPrev) {
  const out = {}; const state = {};
  const routers = ids.filter((id) => net.devices[id].rip);
  // own prefixes
  const tables = {};
  for (const id of routers) {
    const d = net.devices[id];
    tables[id] = {};
    for (const a of upIfs(net, id)) if (ripEnabled(d, a)) tables[id][`${a.net}/${a.prefix}`] = { net: a.net, prefix: a.prefix, metric: 0, via: [], own: true, ifc: a.ifc };
    if (d.rip.defaultOriginate && (ribPrev?.[id] || []).some((r) => r.prefix === 0 && r.src !== 'rip')) tables[id]['0.0.0.0/0'] = { net: '0.0.0.0', prefix: 0, metric: 0, via: [], own: true };
    state[id] = { neighbors: [], sendVersion: ripSends(d.rip), version: d.rip.version };
  }
  // adjacency list: R sends out (ifc) to peer P received on (pIfc)
  const links = [];
  for (const id of routers) {
    const d = net.devices[id];
    for (const a of upIfs(net, id)) {
      if (!ripEnabled(d, a) || d.rip.passive.includes(a.ifc)) continue;
      for (const p of segmentPeers(net, id, a.ifc, a)) {
        const pd = net.devices[p.dev];
        if (!pd.rip) continue;
        const pa = upIfs(net, p.dev).find((x) => x.ifc === p.ifc);
        if (!pa || !ripEnabled(pd, pa)) continue;
        if (!ripAccepts(pd.rip, ripSends(d.rip))) { (state[p.dev].ignored ||= []).push({ from: a.ip, version: ripSends(d.rip), ifc: p.ifc }); continue; }
        links.push({ from: id, fromIfc: a.ifc, fromIp: a.ip, fromNet: a.conNet, to: p.dev, toIfc: p.ifc });
        state[p.dev].neighbors.push({ ip: a.ip, ifc: p.ifc, version: ripSends(d.rip) });
      }
    }
  }
  const summarise = (d, route, sendNet) => {
    const classful = classfulPrefix(route.net);
    const auto = d.rip.version === 2 ? d.rip.autoSummary !== false : true;
    if (!auto || route.prefix === 0 || route.prefix <= classful) return route;
    if (networkOf(route.net, classful) === networkOf(sendNet, classfulPrefix(sendNet))) return route;
    return { ...route, net: networkOf(route.net, classful), prefix: classful };
  };
  for (let round = 0; round < 16; round++) {
    let changed = false;
    for (const l of links) {
      const d = net.devices[l.from];
      for (const r of Object.values(tables[l.from])) {
        if (r.via.some((v) => v.ifc === l.fromIfc)) continue; // split horizon
        const adv = summarise(d, r, l.fromNet);
        const metric = adv.metric + 1;
        if (metric > 15) continue;
        const k = `${adv.net}/${adv.prefix}`;
        const cur = tables[l.to][k];
        const via = { nh: l.fromIp, ifc: l.toIfc };
        if (!cur || (!cur.own && metric < cur.metric)) { tables[l.to][k] = { net: adv.net, prefix: adv.prefix, metric, via: [via] }; changed = true; }
        else if (!cur.own && metric === cur.metric && !cur.via.some((v) => v.nh === via.nh)) { cur.via.push(via); changed = true; }
      }
    }
    if (!changed) break;
  }
  for (const id of routers) {
    out[id] = Object.values(tables[id]).filter((r) => !r.own).flatMap((r) => r.via.map((v) => ({ code: r.prefix === 0 ? 'R*' : 'R', net: r.net, prefix: r.prefix, nh: v.nh, ifc: v.ifc, ad: AD.RIP, metric: r.metric, src: 'rip' })));
    state[id].advertised = Object.values(tables[id]).filter((r) => r.own).map((r) => `${r.net}/${r.prefix}`);
  }
  return { routes: out, state };
}

// ------------------------------------------------------------ OSPF
const areaStr = (a) => String(a);
function ospfIfArea(d, pid, a) {
  const i = d.interfaces[a.ifc];
  if (i?.ospfArea && String(i.ospfArea.pid) === String(pid)) return i.ospfArea.area;
  const p = d.ospf[pid];
  for (const n of p.networks) if (wildcardMatch(a.ip, n.net, n.wild)) return n.area;
  return null;
}
const ospfPassive = (p, ifc) => (p.passiveDefault ? !(p.notPassive || []).includes(ifc) : p.passive.includes(ifc)) || isLoopback(ifc);

/** IP MTU of an interface ("ip mtu <n>"; 1500 by default). */
const ipMtu = (i) => Number(i?.ipMtu) || 1500;
function runOspf(net, ids, ribPrev) {
  const procs = []; // { id, pid, rid, ifs:[{...a, area, cost, passive}] }
  for (const id of ids) {
    const d = net.devices[id];
    for (const [pid, p] of Object.entries(d.ospf || {})) {
      const rid = routerId(net, id, p.routerId);
      if (!rid) continue;
      const ifs = upIfs(net, id).map((a) => ({ ...a, area: ospfIfArea(d, pid, a) })).filter((a) => a.area !== null)
        .map((a) => ({ ...a, cost: ospfCost(a.ifc, a.i), passive: ospfPassive(p, a.ifc), priority: a.i.ospfPriority ?? 1 }));
      procs.push({ id, pid, rid, p, ifs });
    }
  }
  const byDev = {};
  for (const pr of procs) (byDev[pr.id] ||= []).push(pr);
  // adjacencies
  const adj = []; const state = {}; const mismatches = []; const stuck = [];
  for (const pr of procs) state[`${pr.id}|${pr.pid}`] = { rid: pr.rid, pid: pr.pid, neighbors: [], ifs: pr.ifs, areas: [...new Set(pr.ifs.map((x) => areaStr(x.area)))] };
  for (const pr of procs) {
    for (const a of pr.ifs) {
      if (a.passive) continue;
      for (const peer of segmentPeers(net, pr.id, a.ifc, a)) {
        for (const pp of byDev[peer.dev] || []) {
          const b = pp.ifs.find((x) => x.ifc === peer.ifc);
          if (!b || b.passive) continue;
          if (b.mask !== a.mask) { mismatches.push({ dev: pr.id, ifc: a.ifc, from: peer.ip, why: 'mask' }); continue; }
          if (areaStr(b.area) !== areaStr(a.area)) { mismatches.push({ dev: pr.id, ifc: a.ifc, from: peer.ip, why: 'area', theirs: b.area, ours: a.area }); continue; }
          if ((a.i.ospfHello || 10) !== (b.i.ospfHello || 10)) { mismatches.push({ dev: pr.id, ifc: a.ifc, from: peer.ip, why: 'hello' }); continue; }
          if (pp.rid === pr.rid) { mismatches.push({ dev: pr.id, ifc: a.ifc, from: peer.ip, why: 'rid' }); continue; }
          // IP MTU check on Database Description packets: a router drops DBDs whose interface MTU is
          // larger than its own (unless "ip ospf mtu-ignore"), so the adjacency never leaves
          // EXSTART / EXCHANGE — Hellos still work, so the neighbour is listed but never FULL.
          const ma = ipMtu(a.i); const mb = ipMtu(b.i);
          if ((mb > ma && !a.i.ospfMtuIgnore) || (ma > mb && !b.i.ospfMtuIgnore)) {
            mismatches.push({ dev: pr.id, ifc: a.ifc, from: peer.ip, why: 'mtu', theirs: pp.rid });
            stuck.push({ pr, a, pp, b, ip: peer.ip, state: ma > mb ? 'EXSTART' : 'EXCHANGE' });
            continue;
          }
          adj.push({ from: pr, fromIfc: a, to: pp, toIfc: b, nhIp: peer.ip });
        }
      }
    }
  }
  // DR/BDR per segment (keyed by connected network)
  const segs = {};
  for (const pr of procs) for (const a of pr.ifs) if (!a.passive && !isLoopback(a.ifc)) (segs[`${a.conNet}/${a.realPrefix}`] ||= []).push({ rid: a.priority > 0 ? pr.rid : null, priority: a.priority, ip: a.ip, key: `${pr.id}|${pr.pid}|${a.ifc}` });
  const role = {};
  for (const list of Object.values(segs)) {
    const elig = list.filter((x) => x.priority > 0).sort((x, y) => y.priority - x.priority || ipToInt(y.rid) - ipToInt(x.rid));
    list.forEach((x) => { role[x.key] = 'DROTHER'; });
    if (elig[0]) role[elig[0].key] = 'DR';
    if (elig[1]) role[elig[1].key] = 'BDR';
  }
  for (const e of adj) {
    state[`${e.from.id}|${e.from.pid}`].neighbors.push({ rid: e.to.rid, ip: e.nhIp, ifc: e.fromIfc.ifc, state: 'FULL', role: role[`${e.to.id}|${e.to.pid}|${e.toIfc.ifc}`] || 'DROTHER', priority: e.toIfc.priority, area: e.fromIfc.area });
  }
  // neighbours seen in Hellos but stuck in database exchange (MTU mismatch): shown, never used for SPF
  for (const e of stuck) (state[`${e.pr.id}|${e.pr.pid}`].pending ||= []).push({ rid: e.pp.rid, ip: e.ip, ifc: e.a.ifc, state: e.state, role: role[`${e.pp.id}|${e.pp.pid}|${e.b.ifc}`] || 'DROTHER', priority: e.b.priority, area: e.a.area });
  for (const pr of procs) for (const a of pr.ifs) a.role = isLoopback(a.ifc) ? 'LOOP' : a.passive ? 'DR' : role[`${pr.id}|${pr.pid}|${a.ifc}`];
  // external default (default-information originate)
  const asbr = procs.filter((pr) => pr.p.defaultOriginate && (pr.p.always || (ribPrev?.[pr.id] || []).some((r) => r.prefix === 0 && r.src !== 'ospf')));
  // SPF from every process
  const routes = {};
  for (const src of procs) {
    const key = (pr) => `${pr.id}|${pr.pid}`;
    const dist = { [key(src)]: 0 }; const first = { [key(src)]: [] };
    const done = new Set();
    for (;;) {
      let cur = null;
      for (const pr of procs) if (!done.has(key(pr)) && dist[key(pr)] !== undefined && (!cur || dist[key(pr)] < dist[key(cur)])) cur = pr;
      if (!cur) break;
      done.add(key(cur));
      for (const e of adj.filter((x) => x.from === cur)) {
        const nd = dist[key(cur)] + e.fromIfc.cost;
        const hop = cur === src ? [{ nh: e.nhIp, ifc: e.fromIfc.ifc }] : first[key(cur)];
        const k = key(e.to);
        if (dist[k] === undefined || nd < dist[k]) { dist[k] = nd; first[k] = [...hop]; }
        else if (nd === dist[k]) for (const hh of hop) if (!first[k].some((x) => x.nh === hh.nh)) first[k].push(hh);
      }
    }
    const best = {};
    const local = new Set(src.ifs.map((a) => `${a.net}/${a.prefix}`));
    for (const pr of procs) {
      const dk = key(pr);
      if (pr === src || dist[dk] === undefined) continue;
      for (const a of pr.ifs) {
        const pk = `${a.net}/${a.prefix}`;
        if (local.has(pk)) continue;
        const m = dist[dk] + a.cost;
        const inter = !state[key(src)].areas.includes(areaStr(a.area));
        if (!best[pk] || m < best[pk].metric) best[pk] = { net: a.net, prefix: a.prefix, metric: m, via: first[dk], ia: inter };
        else if (m === best[pk].metric) for (const v of first[dk]) if (!best[pk].via.some((x) => x.nh === v.nh)) best[pk].via.push(v);
      }
    }
    for (const as of asbr) {
      const dk = key(as);
      if (as === src || dist[dk] === undefined) continue;
      const m = 1; // E2: metric does not grow
      if (!best['0.0.0.0/0'] || dist[dk] < best['0.0.0.0/0'].fwd) best['0.0.0.0/0'] = { net: '0.0.0.0', prefix: 0, metric: m, via: first[dk], ext: true, fwd: dist[dk] };
    }
    routes[src.id] = (routes[src.id] || []).concat(Object.values(best).flatMap((b) => b.via.map((v) => ({
      code: b.ext ? 'O*E2' : b.ia ? 'O IA' : 'O', net: b.net, prefix: b.prefix, nh: v.nh, ifc: v.ifc, ad: AD.OSPF, metric: b.metric, src: 'ospf', pid: src.pid,
    }))));
  }
  return { routes, state, mismatches };
}

// ------------------------------------------------------------ EIGRP
function eigrpEnabled(p, a) {
  return p.networks.some((n) => (n.wild ? wildcardMatch(a.ip, n.net, n.wild) : networkOf(a.ip, classfulPrefix(a.ip)) === networkOf(n.net, classfulPrefix(n.net))));
}
function runEigrp(net, ids) {
  const procs = [];
  for (const id of ids) {
    const d = net.devices[id];
    for (const [asn, p] of Object.entries(d.eigrp || {})) {
      const ifs = upIfs(net, id).filter((a) => eigrpEnabled(p, a)).map((a) => ({ ...a, bw: ifBandwidth(a.ifc, a.i), delay: ifDelay(a.ifc, a.i), passive: p.passive.includes(a.ifc) || (p.passiveDefault && !(p.notPassive || []).includes(a.ifc)) }));
      procs.push({ id, asn: String(asn), p, ifs, rid: routerId(net, id, p.routerId) });
    }
  }
  const state = {}; const adj = []; const mismatches = [];
  for (const pr of procs) state[`${pr.id}|${pr.asn}`] = { asn: pr.asn, rid: pr.rid, neighbors: [], ifs: pr.ifs, topology: {} };
  for (const pr of procs) {
    for (const a of pr.ifs) {
      if (a.passive) continue;
      for (const peer of segmentPeers(net, pr.id, a.ifc, a)) {
        const pd = net.devices[peer.dev];
        const same = procs.find((x) => x.id === peer.dev && x.asn === pr.asn);
        const b = same?.ifs.find((x) => x.ifc === peer.ifc);
        if (!b) { if (pd.eigrp && Object.keys(pd.eigrp).length) mismatches.push({ dev: pr.id, ifc: a.ifc, from: peer.ip, why: 'as' }); continue; }
        if (b.passive) continue;
        adj.push({ from: pr, fromIfc: a, to: same, toIfc: b, nhIp: peer.ip });
      }
    }
  }
  for (const e of adj) state[`${e.from.id}|${e.from.asn}`].neighbors.push({ ip: e.nhIp, ifc: e.fromIfc.ifc, holdtime: 12 + (ipToInt(e.nhIp) % 3), uptime: '00:14:07', srtt: 1, rto: 100, q: 0, seq: 20 + (ipToInt(e.nhIp) % 50) });
  // distance vector on (bwMin, delaySum)
  const key = (pr) => `${pr.id}|${pr.asn}`;
  const tab = {};
  for (const pr of procs) {
    tab[key(pr)] = {};
    for (const a of pr.ifs) tab[key(pr)][`${a.net}/${a.prefix}`] = { net: a.net, prefix: a.prefix, bw: a.bw, delay: a.delay, metric: eigrpMetric(a.bw, a.delay), own: true };
  }
  for (let round = 0; round < 20; round++) {
    let changed = false;
    for (const pr of procs) {
      const T = tab[key(pr)];
      const cand = {};
      for (const e of adj.filter((x) => x.from === pr)) {
        for (const [k, r] of Object.entries(tab[key(e.to)])) {
          if (T[k]?.own) continue;
          const bw = Math.min(r.bw, e.fromIfc.bw); const delay = r.delay + e.fromIfc.delay;
          (cand[k] ||= []).push({ net: r.net, prefix: r.prefix, bw, delay, metric: eigrpMetric(bw, delay), rd: r.metric, nh: e.nhIp, ifc: e.fromIfc.ifc });
        }
      }
      for (const [k, list] of Object.entries(cand)) {
        list.sort((x, y) => x.metric - y.metric);
        const b = list[0];
        const cur = T[k];
        if (!cur || cur.metric !== b.metric || cur.bw !== b.bw) { T[k] = { net: b.net, prefix: b.prefix, bw: b.bw, delay: b.delay, metric: b.metric, via: list.filter((x) => x.metric === b.metric), all: list }; changed = true; }
        else { cur.via = list.filter((x) => x.metric === b.metric); cur.all = list; }
      }
      for (const k of Object.keys(T)) if (!T[k].own && !cand[k]) { delete T[k]; changed = true; }
    }
    if (!changed) break;
  }
  const routes = {};
  for (const pr of procs) {
    const T = tab[key(pr)];
    const st = state[key(pr)];
    routes[pr.id] = (routes[pr.id] || []).concat(Object.values(T).filter((r) => !r.own).flatMap((r) => r.via.map((v) => ({ code: 'D', net: r.net, prefix: r.prefix, nh: v.nh, ifc: v.ifc, ad: AD.EIGRP, metric: r.metric, src: 'eigrp', asn: pr.asn }))));
    for (const r of Object.values(T)) {
      const fd = r.metric;
      st.topology[`${r.net}/${r.prefix}`] = r.own
        ? { fd, own: true, ifc: pr.ifs.find((a) => `${a.net}/${a.prefix}` === `${r.net}/${r.prefix}`)?.ifc, successors: 1 }
        : { fd, successors: r.via.length, paths: r.all.map((x) => ({ nh: x.nh, ifc: x.ifc, metric: x.metric, rd: x.rd, successor: x.metric === fd, feasible: x.metric !== fd && x.rd < fd })) };
    }
  }
  return { routes, state, mismatches };
}

// ------------------------------------------------------------ BGP
function runBgp(net, ids, ribPrev) {
  const spk = ids.filter((id) => net.devices[id].bgp?.asn);
  const state = {}; const sessions = [];
  for (const id of spk) {
    const b = net.devices[id].bgp;
    state[id] = { asn: b.asn, rid: routerId(net, id, b.routerId), neighbors: {}, table: {} };
  }
  for (const id of spk) {
    const b = net.devices[id].bgp;
    const ifs = upIfs(net, id);
    for (const [ip, n] of Object.entries(b.neighbors || {})) {
      const st = { ip, remoteAs: n.remoteAs, state: 'Idle', pfxRcd: 0, up: null, msgRcvd: 0, msgSent: 0 };
      state[id].neighbors[ip] = st;
      if (n.shutdown) { st.state = 'Idle (Admin)'; continue; }
      const local = ifs.find((a) => !isLoopback(a.ifc) && inSubnet(ip, a.conNet, a.realPrefix));
      if (!local) continue;  // no route to the neighbour → Idle
      const peer = segmentPeers(net, id, local.ifc, local).find((p) => p.ip === ip);
      if (!peer) { st.state = 'Active'; continue; }
      const pb = net.devices[peer.dev].bgp;
      const back = pb?.neighbors?.[local.ip];
      if (!pb?.asn || !back || back.shutdown || String(back.remoteAs) !== String(b.asn) || String(n.remoteAs) !== String(pb.asn)) { st.state = 'Active'; if (pb?.asn && String(n.remoteAs) !== String(pb.asn)) st.badAs = pb.asn; continue; }
      st.state = 'Established'; st.peer = peer.dev; st.localIp = local.ip; st.ifc = local.ifc;
      st.ebgp = String(pb.asn) !== String(b.asn);
      sessions.push({ from: id, to: peer.dev, toIp: ip, fromIp: local.ip, ifc: local.ifc, peerIfc: peer.ifc, defaultOriginate: !!n.defaultOriginate, ebgp: st.ebgp });
    }
  }
  // origination
  const loc = {};
  for (const id of spk) {
    const b = net.devices[id].bgp;
    loc[id] = {};
    const rib = (ribPrev?.[id] || []).filter((r) => r.src !== 'bgp');
    for (const n of b.networks || []) {
      const p = maskToPrefix(n.mask);
      if (!rib.some((r) => r.net === n.net && r.prefix === p && r.code !== 'L')) continue; // must exist exactly in the RIB
      loc[id][`${n.net}/${p}`] = { net: n.net, prefix: p, path: [], nh: '0.0.0.0', weight: 32768, origin: 'i', local: true, best: true };
    }
  }
  for (let round = 0; round < 12; round++) {
    let changed = false;
    for (const s of sessions) {
      const sb = net.devices[s.from].bgp; const rb = net.devices[s.to].bgp;
      const adv = Object.values(loc[s.from]).filter((r) => (s.ebgp ? true : !r.ibgp || r.local));
      if (s.defaultOriginate) adv.push({ net: '0.0.0.0', prefix: 0, path: [], origin: 'i', local: true });
      for (const r of adv) {
        const path = s.ebgp ? [String(sb.asn), ...r.path] : [...r.path];
        if (path.includes(String(rb.asn)) && s.ebgp) continue; // AS-path loop prevention
        const k = `${r.net}/${r.prefix}`;
        const cand = { net: r.net, prefix: r.prefix, path, nh: s.fromIp, from: s.from, peerIp: s.fromIp, ifc: s.peerIfc, ebgp: s.ebgp, ibgp: !s.ebgp, weight: 0, origin: r.origin || 'i', peerRid: state[s.from].rid };
        const cur = loc[s.to][k];
        let replace = false;
        if (!cur) replace = true;
        else if (cur.local) replace = false;
        else if (cur.from === cand.from) replace = cur.path.join() !== cand.path.join() || cur.nh !== cand.nh;
        else replace = cand.path.length < cur.path.length
          || (cand.path.length === cur.path.length && cand.ebgp && !cur.ebgp)
          || (cand.path.length === cur.path.length && cand.ebgp === cur.ebgp && ipToInt(cand.peerRid) < ipToInt(cur.peerRid));
        if (replace) { loc[s.to][k] = cand; changed = true; }
        (state[s.to].table[k] ||= {})[s.fromIp] = cand;
      }
    }
    if (!changed) break;
  }
  const routes = {};
  for (const id of spk) {
    for (const [k, r] of Object.entries(loc[id])) {
      state[id].table[k] = { ...(state[id].table[k] || {}), [r.local ? '0.0.0.0' : r.peerIp]: { ...r, best: true } };
    }
    for (const s of sessions.filter((x) => x.to === id)) {
      state[id].neighbors[s.fromIp] && (state[id].neighbors[s.fromIp].pfxRcd = Object.values(state[id].table).filter((t) => t[s.fromIp]).length);
    }
    routes[id] = Object.values(loc[id]).filter((r) => !r.local).map((r) => ({ code: r.prefix === 0 ? 'B*' : 'B', net: r.net, prefix: r.prefix, nh: r.nh, ifc: r.ifc, ad: r.ebgp ? AD.eBGP : AD.iBGP, metric: 0, src: 'bgp', path: r.path }));
  }
  return { routes, state };
}

// ------------------------------------------------------------ RIB assembly
function select(routes) {
  const best = {};
  for (const r of routes) {
    const k = `${r.net}/${r.prefix}/${r.code === 'L' ? 'L' : ''}`;
    const cur = best[k];
    if (!cur || r.ad < cur[0].ad || (r.ad === cur[0].ad && r.src === cur[0].src && r.metric < cur[0].metric)) best[k] = [r];
    else if (r.ad === cur[0].ad && r.src === cur[0].src && r.metric === cur[0].metric && r.src !== 'connected' && r.src !== 'local'
      && !cur.some((x) => x.nh === r.nh && x.ifc === r.ifc)) cur.push(r);
  }
  return Object.values(best).flat();
}

const VOLATILE = new Set(['ticks', 'fhrpMem', 'natBindings', 'natStats', 'arp', 'macTable', 'counters', 'logs', 'translations', 'bindings', 'connections', 'lease', 'startup', 'startupState', 'description', 'http', 'dnsRecords', 'services']);
export function signature(net) {
  return JSON.stringify(net, (k, v) => (VOLATILE.has(k) ? undefined : v));
}

const memo = new WeakMap();
/** Full routing result for every L3 device: { rib:{id:[routes]}, rip, ospf, eigrp, bgp }. */
export function converge(net) {
  const sig = signature(net);
  const m = memo.get(net);
  if (m && m.sig === sig) return m.res;
  const ids = Object.keys(net.devices).filter((id) => isRouterLike(net.devices[id]));
  const base = Object.fromEntries(ids.map((id) => [id, baseRoutes(net, id)]));
  let rib = base;
  let rip, ospf, eigrp, bgp;
  for (let pass = 0; pass < 3; pass++) {
    rip = runRip(net, ids, rib);
    ospf = runOspf(net, ids, rib);
    eigrp = runEigrp(net, ids);
    bgp = runBgp(net, ids, rib);
    const next = Object.fromEntries(ids.map((id) => [id, select([...base[id], ...(rip.routes[id] || []), ...(ospf.routes[id] || []), ...(eigrp.routes[id] || []), ...(bgp.routes[id] || [])])]));
    const same = JSON.stringify(next) === JSON.stringify(rib);
    rib = next;
    if (same) break;
  }
  const res = { rib, rip, ospf, eigrp, bgp };
  memo.set(net, { sig, res });
  return res;
}

export { intToIp, prefixToMask, INF };
